import "@ui5/webcomponents-react/dist/Assets.js";
import "@positive/ui/fonts.css";
import "./styles/global.css";

import { createI18n } from "@positive/i18n";
import { applyTheme, registerFonts, switchLanguage } from "@positive/ui";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { createAppApiClient } from "./app/apiClient";
import { App } from "./app/App";
import { AuthService } from "./app/identity/authService";
import { createOidcConfig, identityDefaults } from "./app/identity/config";
import { resolveHostMode, type HostMode } from "./app/identity/host";
import { readSupportSession } from "./app/identity/supportSession";
import { createUserManager } from "./app/identity/userManager";
import { navigationFor } from "./app/navigation";
import { loadPreferences } from "./app/preferences";
import { createAppRouter, type AppRouterOptions } from "./app/router";

type Extra = Pick<AppRouterOptions, "extra">;

async function loadDevExtras(i18nInstance: Awaited<ReturnType<typeof createI18n>>): Promise<Extra> {
  // Statically false in production builds: the dev module and its mock data are dropped from the bundle
  // (verified by scripts/check-production-bundle.mjs after every build).
  if (!import.meta.env.DEV) return {};
  const dev = await import("./dev");
  dev.registerDevResources(i18nInstance);
  return { extra: { routes: dev.createDevRoutes, navigation: dev.devNavigation } };
}

function hostMode(): HostMode {
  return resolveHostMode(window.location.hostname, {
    rootDomain: import.meta.env.VITE_TENANT_ROOT_DOMAIN ?? identityDefaults.rootDomain,
    // Development convenience only; never honoured by production builds.
    devTenant: import.meta.env.DEV ? import.meta.env.VITE_DEV_TENANT : undefined,
  });
}

function sessionStorageOrNull(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

async function bootstrap(): Promise<void> {
  registerFonts();
  const mode = hostMode();
  const supportSessionId =
    mode.kind === "tenant" ? readSupportSession(window.location.search, sessionStorageOrNull()) : null;
  const auth = new AuthService({
    oidc: createUserManager(createOidcConfig(mode, window.location.origin, import.meta.env, supportSessionId !== null)),
    location: window.location,
    replaceUrl: (url) => {
      window.history.replaceState(null, "", url);
    },
    // Keycloak's login page in the language last used here (the account's language after a previous sign-in).
    uiLocale: () => loadPreferences().language,
  });
  // Callback pages (silent renew iframe, re-authentication popup) only hand the result to the opener.
  const started = auth.start();

  const preferences = loadPreferences();
  const i18n = await createI18n({ language: preferences.language });
  await Promise.all([switchLanguage(i18n, preferences.language), applyTheme(preferences.theme)]);
  if ((await started).status === "callback") return;

  const router = createAppRouter({ ...(await loadDevExtras(i18n)), mode, navigation: navigationFor(mode) });
  const container = document.getElementById("root");
  if (!container) {
    throw new Error("Root element #root is missing");
  }
  createRoot(container).render(
    <StrictMode>
      <App
        i18n={i18n}
        apiClient={createAppApiClient({ i18n, auth, mode, supportSessionId })}
        router={router}
        initialPreferences={preferences}
        auth={auth}
        mode={mode}
        supportSessionId={supportSessionId}
      />
    </StrictMode>,
  );
}

void bootstrap();
